import axios, { AxiosResponse } from 'axios';
import { 
  LoginRequest, 
  LoginResponse,
  SignupRequest,
  SignupResponse,
  VerifyTokenResponse,
  ProductsResponse, 
  WheelsResponse, 
  SpinsResponse,
  Product,
  Wheel,
  WheelData,
  WheelStatus,
  SpinRequest,
  SpinResponse,
  ApiError,
  User,
  Company,
  SubscriptionPlan
} from '../types';
import { normalizeCsvData } from '../utils/normalize';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Configurar axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentCompany');
      // window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Função helper para tratar erros
const handleApiError = (error: any): ApiError => {
  if (error.response?.data) {
    return error.response.data;
  }
  return {
    error: 'Erro de conexão',
    details: error.message
  };
};

// AUTH ENDPOINTS
export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response: AxiosResponse<LoginResponse> = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  signup: async (data: SignupRequest): Promise<SignupResponse> => {
    try {
      const response: AxiosResponse<SignupResponse> = await api.post('/auth/signup', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  verifyToken: async (): Promise<VerifyTokenResponse> => {
    try {
      const response: AxiosResponse<VerifyTokenResponse> = await api.get('/auth/verify');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  changePassword: async (data: { currentPassword: string; newPassword: string }) => {
    try {
      const response = await api.post('/auth/change-password', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// USERS ENDPOINTS
export const usersApi = {
  getAll: async (params?: { page?: number; limit?: number }): Promise<{ users: User[]; pagination: any }> => {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  create: async (data: { email: string; name: string; password: string; role: string }): Promise<{ user: User }> => {
    try {
      const response = await api.post('/users', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  update: async (id: number, data: { name?: string; role?: string; is_active?: boolean }): Promise<{ user: User }> => {
    try {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async (id: number): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// COMPANIES ENDPOINTS
export const companiesApi = {
  getCurrent: async (): Promise<{ company: Company }> => {
    try {
      const response = await api.get('/companies/current');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  update: async (data: Partial<Company>): Promise<{ company: Company }> => {
    try {
      const response = await api.put('/companies/current', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// SUBSCRIPTION PLANS ENDPOINTS
export const plansApi = {
  getAll: async (): Promise<{ plans: SubscriptionPlan[] }> => {
    try {
      const response = await api.get('/plans');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// PRODUCTS ENDPOINTS
export const productsApi = {
  getAll: async (params?: { 
    active?: boolean; 
    search?: string; 
    page?: number; 
    limit?: number; 
  }): Promise<ProductsResponse> => {
    try {
      const response: AxiosResponse<ProductsResponse> = await api.get('/products', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getById: async (id: number): Promise<Product> => {
    try {
      const response: AxiosResponse<Product> = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  create: async (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<{ product: Product }> => {
    try {
      const response = await api.post('/products', product);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  update: async (id: number, product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<{ product: Product }> => {
    try {
      const response = await api.put(`/products/${id}`, product);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateStock: async (id: number, stock: number): Promise<{ product: Product }> => {
    try {
      const response = await api.patch(`/products/${id}/stock`, { stock });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  toggleActive: async (id: number): Promise<{ product: Product }> => {
    try {
      const response = await api.patch(`/products/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  uploadImage: async (file: File, productId?: number): Promise<{ imageUrl: string }> => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const url = productId ? `/products/upload/${productId}` : '/products/upload';
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteImage: async (id: number): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/products/${id}/image`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async (id: number): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// WHEELS ENDPOINTS
export const wheelsApi = {
  getAll: async (params?: { 
    status?: string; 
    search?: string; 
    page?: number; 
    limit?: number; 
  }): Promise<WheelsResponse> => {
    try {
      const response: AxiosResponse<WheelsResponse> = await api.get('/wheels', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getById: async (id: number): Promise<Wheel> => {
    try {
      const response: AxiosResponse<Wheel> = await api.get(`/wheels/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  create: async (wheel: Omit<Wheel, 'id' | 'created_at' | 'updated_at'>): Promise<{ wheel: Wheel }> => {
    try {
      const response = await api.post('/wheels', wheel);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  update: async (id: number, wheel: Omit<Wheel, 'id' | 'created_at' | 'updated_at'>): Promise<{ wheel: Wheel }> => {
    try {
      const response = await api.put(`/wheels/${id}`, wheel);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  addProducts: async (id: number, products: { product_id: number; odds_weight: number }[]): Promise<{ message: string }> => {
    try {
      const response = await api.post(`/wheels/${id}/products`, { products });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  removeProduct: async (wheelId: number, productId: number): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/wheels/${wheelId}/products/${productId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getProducts: async (id: number): Promise<{ products: Product[] }> => {
    try {
      const response: AxiosResponse<{ products: Product[] }> = await api.get(`/wheels/${id}/products`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getSpins: async (id: number, params?: { page?: number; limit?: number }): Promise<SpinsResponse> => {
    try {
      const response: AxiosResponse<SpinsResponse> = await api.get(`/wheels/${id}/spins`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getAllSpins: async (params?: { page?: number; limit?: number }): Promise<SpinsResponse> => {
    try {
      const response: AxiosResponse<SpinsResponse> = await api.get(`/wheels/spins/all`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  generateQRCode: async (id: number, params?: { format?: 'png' | 'svg'; size?: number }): Promise<Blob> => {
    try {
      const response = await api.get(`/wheels/${id}/qrcode`, { 
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  generateQRDataURL: async (id: number, params?: { size?: number }): Promise<{ qr_data_url: string; qr_url: string }> => {
    try {
      const response = await api.get(`/wheels/${id}/qrcode/data`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async (id: number): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/wheels/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// PUBLIC ENDPOINTS
export const publicApi = {
  getWheelStatus: async (slug: string): Promise<WheelStatus> => {
    try {
      const response: AxiosResponse<WheelStatus> = await axios.get(`${API_BASE_URL}/public/wheels/${slug}/status`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getWheelData: async (companySlug: string, slug: string): Promise<WheelData> => {
    try {
      const response: AxiosResponse<WheelData> = await axios.get(`${API_BASE_URL}/public/${companySlug}/wheels/${slug}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  checkWhatsApp: async (companySlug: string, slug: string, whatsapp: string): Promise<{ 
    can_spin: boolean; 
    spins_count: number;
    spins_limit: number;
    has_limit: boolean;
    whatsapp: string;
    message?: string;
  }> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/${companySlug}/wheels/${slug}/check-whatsapp`, {
        params: { whatsapp }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  spin: async (companySlug: string, slug: string, data: SpinRequest): Promise<SpinResponse> => {
    try {
      const response: AxiosResponse<SpinResponse> = await axios.post(`${API_BASE_URL}/public/${companySlug}/wheels/${slug}/spin`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getRecentSpins: async (slug: string, limit = 10): Promise<{ recent_spins: any[]; count: number }> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/wheels/${slug}/recent-spins`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// WHATSAPP ENDPOINTS
export const whatsappApi = {
  // Connection
  getConnectionStatus: async () => {
    try {
      const response = await api.get('/whatsapp/connection/status');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  generateQRCode: async () => {
    try {
      const response = await api.post('/whatsapp/connection/generate-qr');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  disconnect: async () => {
    try {
      const response = await api.post('/whatsapp/connection/disconnect');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Templates
  getTemplates: async (activeOnly = false) => {
    try {
      const response = await api.get('/whatsapp/templates', {
        params: { active_only: activeOnly }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getTemplate: async (id: number) => {
    try {
      const response = await api.get(`/whatsapp/templates/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  createTemplate: async (data: {
    name: string;
    type: 'text' | 'image' | 'video' | 'audio' | 'voice';
    content?: string;
    media_url?: string;
    delay_type?: 'auto' | 'random' | 'fixed';
    delay_seconds?: number;
  }) => {
    try {
      const response = await api.post('/whatsapp/templates', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateTemplate: async (id: number, data: {
    name?: string;
    type?: 'text' | 'image' | 'video' | 'audio' | 'voice';
    content?: string;
    media_url?: string;
    delay_type?: 'auto' | 'random' | 'fixed';
    delay_seconds?: number;
    is_active?: boolean;
  }) => {
    try {
      const response = await api.put(`/whatsapp/templates/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteTemplate: async (id: number) => {
    try {
      const response = await api.delete(`/whatsapp/templates/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Dispatches
  listDispatches: async (page = 1, limit = 20) => {
    try {
      const response = await api.get('/whatsapp/dispatches', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  dispatchFromCSV: async (templateId: number, csvData: any[]) => {
    try {
      const response = await api.post('/whatsapp/dispatch/csv', {
        template_id: templateId,
        csv_data: normalizeCsvData(csvData)
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  dispatchFromLeads: async (templateId: number, leadIds: number[]) => {
    try {
      const response = await api.post('/whatsapp/dispatch/leads', {
        template_id: templateId,
        lead_ids: leadIds
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  processDispatch: async (dispatchId: number) => {
    try {
      const response = await api.post(`/whatsapp/dispatch/${dispatchId}/process`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getDispatchStatus: async (dispatchId: number) => {
    try {
      const response = await api.get(`/whatsapp/dispatch/${dispatchId}/status`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getDispatchItems: async (dispatchId: number, page = 1, limit = 50, status?: string) => {
    try {
      const response = await api.get(`/whatsapp/dispatch/${dispatchId}/items`, {
        params: { page, limit, status }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export default api;